import { useEffect, useState } from 'react';
import styled from 'styled-components';
import EyeHideIcon from '../svgIcons/EyeHideIcon';
import EyeIcon from '../svgIcons/EyeIcon';
import { ErrorMessage, Field } from 'formik';
import { formErrorMessage } from './FormErrorMessage';
import StyledInputWithIcon from '../styledComponents/StyledInputWithIcon';
import Proptypes from 'prop-types';

const formatValue = (value, formatType) => {
  if (!value && value !== 0) return '';
  let result;
  switch (formatType) {
    case 'DECIMAL': {
      if (!isNaN(value)) {
        result = parseFloat(value).toFixed(2);
      } else {
        result = '';
      }
      break;
    }
    case 'NUMBER': {
      if (!isNaN(value)) {
        result = parseFloat(value).toString();
      } else {
        result = '';
      }
      break;
    }
    case 'INTEGER': {
      if (!isNaN(value)) {
        result = Math.floor(value).toString();
      } else {
        result = '';
      }
      break;
    }
    default: {
      result = value;
      break;
    }
  }
  return result;
};

const resolveValue = (value, formatType, currentValue) => {
  if (!value && value !== 0) return '';
  let result;
  switch (formatType) {
    case 'DECIMAL': {
      const tempValue = value;
      if (!isNaN(tempValue)) {
        result = tempValue;
      } else {
        result = '';
      }
      break;
    }
    case 'NUMBER': {
      if (!isNaN(value)) {
        result = value;
      } else if (value === '-') {
        result = '-';
      } else if (value === '') {
        result = '';
      } else {
        result = currentValue;
      }
      break;
    }
    case 'INTEGER': {
      if (!isNaN(value)) {
        result = Math.floor(value).toString();
      } else if (value === '-') {
        result = '-';
      } else if (value === '') {
        result = '';
      } else {
        result = currentValue;
      }
      break;
    }
    default: {
      result = value;
      break;
    }
  }
  return result;
};

const resolveValueForSave = (value, formatType) => {
  if (!value && value !== 0) return '';
  let result;
  switch (formatType) {
    case 'DECIMAL': {
      result = parseFloat(parseFloat(value).toFixed(2));
      break;
    }
    case 'NUMBER': {
      if (!isNaN(value)) {
        result = parseFloat(value);
      } else {
        result = '';
      }
      break;
    }
    case 'INTEGER': {
      if (!isNaN(value)) {
        result = Math.floor(value);
      } else {
        result = '';
      }
      break;
    }
    default: {
      result = value;
      break;
    }
  }
  return result;
};

const InputField = ({
  placeholder,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  type = 'text',
  readOnly = false,
  disabled = false,
  formatType,
  $fontSize = '',
  $phFontSize = '',
  $phColor = '',
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const handleChange = (e) => {
    const value = resolveValue(e.target.value, formatType, currentValue);
    setCurrentValue(value);
  };

  const handleKeypress = (e) => {
    if (e.keyCode === 13) {
      e.target.blur();
    }
  };

  const handleBlur = (e) => {
    const resolvedValue = resolveValueForSave(currentValue, formatType);
    const formattedValue = formatValue(resolvedValue, formatType);
    setCurrentValue(formattedValue);

    onBlur && onBlur(e);
    onChange(resolvedValue);
  };

  const handleFocus = (e) => {
    const unformattedValue = resolveValue(
      currentValue,
      formatType,
      currentValue,
    );
    setCurrentValue(unformattedValue);
    onFocus && onFocus(e);
  };

  useEffect(() => {
    const formattedValue = formatValue(value, formatType);
    setCurrentValue(formattedValue);
  }, [value, formatType]);

  return (
    <>
      <StyledInputWithIcon
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        name={name}
        value={currentValue ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeypress}
        disabled={disabled}
        readOnly={readOnly}
        {...(placeholder ? { placeholder: placeholder } : {})}
        $fontSize={$fontSize}
        $phFontSize={$phFontSize}
        $phColor={$phColor}
        {...props}
      />
      {type === 'password' && (
        <div className="position-relative">
          <StyledIcon
            className="position-absolute cursor-pointer"
            style={{
              top: '-22px',
              right: '10px',
              transform: 'translateY(-50%)',
            }}
            onClick={toggleShowPassword}
          >
            {showPassword ? <EyeHideIcon /> : <EyeIcon />}
          </StyledIcon>
        </div>
      )}
    </>
  );
};

const FormikInputWithIcon = ({
  name,
  type = 'text',
  onChange,
  $fontSize = '',
  $phFontSize = '',
  $phColor = '',
  hideError = false,
  errorTextUpdate = { current: '', updated: '' },
  ...props
}) => {
  return (
    <Field name={name}>
      {({ field, form: { touched, errors, submitCount, setFieldValue } }) => (
        <>
          <InputField
            isinvalid={submitCount && touched[field.name] && errors[field.name]}
            value={field?.value}
            name={name}
            type={type}
            onChange={(val) => {
              setFieldValue(name, val);
              onChange && onChange(val);
            }}
            $fontSize={$fontSize}
            $phFontSize={$phFontSize}
            $phColor={$phColor}
            {...props}
          />
          {submitCount > 0 && !hideError && (
            <ErrorMessage
              name={field.name}
              render={(msg) =>
                formErrorMessage(
                  errorTextUpdate?.current &&
                    errorTextUpdate?.updated &&
                    msg?.includes(errorTextUpdate?.current)
                    ? msg?.replace(
                        errorTextUpdate?.current,
                        errorTextUpdate?.updated,
                      )
                    : msg,
                )
              }
            />
          )}
        </>
      )}
    </Field>
  );
};

const StyledIcon = styled.div`
  color: ${({ theme }) => theme.primaryDark};
`;

FormikInputWithIcon.propTypes = {
  name: Proptypes.string.isRequired,
  placeholder: Proptypes.string,
  type: Proptypes.string,
  as: Proptypes.string,
  rows: Proptypes.string,
  formatType: Proptypes.string,
  hideError: Proptypes.bool,
};

export default FormikInputWithIcon;
